// ignore-string-externalization
import React, { useState } from 'react';
import {
  createI18n,
  defaultNamespace,
  I18NContext,
  LocaleProvider,
} from '@spotify-internal/i18n-core';
import { Resource } from 'i18next';

// get initial store passed down from the server
const hydratedStore: Resource | undefined = process.browser
  ? JSON.parse(document.querySelector('#__I18N_DATA__')?.textContent ?? '{}')
  : {};

function getI18nStore() {
  // use the hydratedStore on the client
  if (process.browser) return hydratedStore;
  // use an empty store on the server.
  return {};
}

const translationsBackend = {
  type: 'backend' as const,
  async read(lang: string, _ns: any, callback: any) {
    try {
      // fallback to inline strings when loading english
      if (lang === 'en') {
        return callback(null, {});
      }

      return callback(
        null,
        await import(`./translations/${lang}/strings.json`),
      );
    } catch (err) {
      // fallback to inlined strings on error
      return callback(null, {});
    }
  },
};

export const I18nProvider = ({
  children,
  locale,
}: React.PropsWithChildren<{ locale?: string }>) => {
  const derivedLocale = locale ?? 'en';
  const _i18nStore = getI18nStore();
  const [i18n] = useState(() => createI18n(_i18nStore, translationsBackend));

  // synchronously preload default namespace on the server
  if (!process.browser) {
    let translations: any;

    try {
      translations =
        derivedLocale !== 'en'
          ? require(`./translations/${derivedLocale}/strings.json`)
          : {};
    } catch (err) {
      translations = {};
    }

    i18n.addResources(derivedLocale, defaultNamespace, translations);
  }

  const serializedState = React.useMemo(
    () => JSON.stringify(_i18nStore),
    [_i18nStore],
  );

  return (
    <I18NContext.Provider value={i18n}>
      <LocaleProvider value={derivedLocale}>
        {children}
        {/** pass down initial store from the server to the client */}
        <script
          id="__I18N_DATA__"
          type="application/json"
          dangerouslySetInnerHTML={{
            __html: serializedState,
          }}
        />
      </LocaleProvider>
    </I18NContext.Provider>
  );
};
